import React, { useState } from 'react';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import ImageList from '@mui/material/ImageList';
import IconButton from '@mui/material/IconButton';
import ImageListItem from '@mui/material/ImageListItem';
import Box from '@mui/material/Box';

import Dialog from 'src/components/ui/Dialog';
import Icon from 'src/components/ui/Icon';
import MediaCarousel from 'src/components/ui/MediaCarousel';
import ExpandableText from 'src/components/ui/ExpandableText';
import Typography from 'src/components/ui/Typography';
import MediumComponent from 'src/components/other/MediumComponent';

import type { ModalProps } from 'src/hooks/useModalOpenState';

import type { Medium, Project } from 'src/types/__generated__/graphql';
import { styledComponent } from 'src/utils/styled';

const StyledGridContainer = styledComponent(Grid)`
    min-height: 32rem;
`;

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        min-width: 25.9375rem;
        position: relative;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: 'md',
    fullWidth: true,
};

const CloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
`;

const RightSectionContainer = styledComponent(Card)`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    .MuiCardHeader-content {
        max-width: calc(100% - 6rem); // 6 rem = space for menu and heart
    }
`;

const StyledCardContent = styledComponent(CardContent)`
    padding-top: 0.5rem;
    overflow-y: scroll;
`;

const StyledGridListTile = styledComponent(ImageListItem)`
    & > div {
        border-radius: 0.25rem;
    }
    * {
        height: 100%;
    }
`;

const ProjectName = styledComponent(Typography)`
    margin-bottom: 0.5rem;
`;
ProjectName.defaultProps = { variant: 'h2' };

const ProjectCityContainer = styledComponent(Box)``;
ProjectCityContainer.defaultProps = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
};

const ProjectCity = styledComponent(Typography)`
    margin-left: 0.375rem;
`;
ProjectCity.defaultProps = { variant: 'h5', color: 'dark', weight: 'normal' };

interface Props extends ModalProps {
    project?: Project;
}

const ProjectModal: React.FC<Props> = ({ open, onClose, project }) => {
    const hasMedia = project?.media.length ? project.media.length > 0 : false;

    const [showedImage, setShowedImage] = useState(0);

    const handleClose = () => {
        onClose();
        setShowedImage(0);
    };

    return (
        <StyledDialog onClose={handleClose} open={open} fullWidth={hasMedia}>
            <StyledGridContainer container>
                {hasMedia && (
                    <Grid sm={7} item>
                        <MediaCarousel
                            media={project?.media as Medium[]}
                            alt={String(project?.name)}
                            objectFit="contain"
                            selectedItem={showedImage}
                            onSelectedItemChange={setShowedImage}
                        />
                    </Grid>
                )}
                <Grid sm={hasMedia ? 5 : 12} item>
                    <RightSectionContainer>
                        <Box>
                            <CardHeader
                                title={
                                    <>
                                        <ProjectName>{project?.name}</ProjectName>
                                        <ProjectCityContainer>
                                            <Icon size="1rem" name="Location" />
                                            <ProjectCity>{project?.city.name}</ProjectCity>
                                        </ProjectCityContainer>
                                    </>
                                }
                            />
                            <StyledCardContent>
                                <ExpandableText text={project?.description} />
                            </StyledCardContent>
                        </Box>
                        {hasMedia && (
                            <ImageList rowHeight={90} cols={4} gap={8} style={{ margin: '0.75rem' }} component="div">
                                {
                                    project?.media.map((media, index) => (
                                        <StyledGridListTile
                                            key={media?.url}
                                            cols={1}
                                            onClick={() => setShowedImage(index)}
                                        >
                                            <MediumComponent
                                                cropWidth={172}
                                                cropHeight={180}
                                                objectFit="cover"
                                                media={media}
                                            />
                                        </StyledGridListTile>
                                    )) as NonNullable<React.ReactNode>
                                }
                            </ImageList>
                        )}
                    </RightSectionContainer>
                </Grid>
            </StyledGridContainer>
            <CloseButton aria-label="close" onClick={handleClose}>
                <Icon name="Close" />
            </CloseButton>
        </StyledDialog>
    );
};

export default ProjectModal;
